<template>
  <div
    v-if="order"
    class="configurator order-detail"
  >
    <!-- Cabecera -->
    <div class="configurator-actions">
      <div class="d-flex align-items-center">
        <b-button
          variant="outline-secondary"
          class="btn-icon mr-1"
          @click="back"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="14"
          />
        </b-button>
        <div>
          <div class="configurator-title">
            {{ $t('configurator.detail.offer') }} {{ offerNumber }}
          </div>
          <div class="configurator-subtitle">
            {{ [order.client && order.client.name, created].filter(Boolean).join(' · ') }}
          </div>
        </div>
        <span
          class="cfg-badge dot ml-1"
          :class="statusClass"
        >{{ $t(`configurator.order_status.${status}`) }}</span>
      </div>
    </div>
    <!-- Seguimiento -->
    <div class="cfg-card">
      <order-tracking :order="order" />
    </div>

    <!-- Siguiente paso: qué toca ahora, quién y con qué botón (incluye la revisión de Tecnitex) -->
    <next-step
      :order="order"
      @go-lines="goToLines"
    />

    <!-- Líneas: a todo el ancho, como el paso 4 del configurador -->
    <div
      v-if="legacy"
      class="budget-note mb-1"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="14"
      />
      {{ $t('configurator.detail.legacy_note') }}
    </div>
    <div
      ref="lines"
      :class="{ 'lines-highlight': highlightLines }"
    >
      <offer-lines
        :order="order"
        :state="configurator"
        :price-editable="isSuperAdmin && inRevision"
        @set-price="setLinePrice"
      />
    </div>

    <!-- Debajo: datos, entrega y documentos a la izquierda; totales a la derecha -->
    <div class="detail-bottom">
      <div class="detail-cards">
        <!-- Datos -->
        <div class="cfg-card">
          <div class="budget-header">
            <span class="budget-title">{{ $t('configurator.detail.data') }}</span>
          </div>
          <div class="budget-stats border-0">
            <div class="budget-stat">
              <span class="budget-stat-label">{{ $t('configurator.list.number') }}</span>
              <span class="budget-stat-value">{{ offerNumber }}</span>
            </div>
            <div class="budget-stat">
              <span class="budget-stat-label">{{ $t('configurator.list.date') }}</span>
              <span class="budget-stat-value">{{ created }}</span>
            </div>
            <div class="budget-stat">
              <span class="budget-stat-label">{{ $t('configurator.list.updated_at') }}</span>
              <span class="budget-stat-value">{{ date(order.updated_at) }}</span>
            </div>
            <div
              v-if="showClient && order.client"
              class="budget-stat"
            >
              <span class="budget-stat-label">{{ $t('configurator.list.client') }}</span>
              <b-link
                v-if="isSuperAdmin"
                :to="{ name: 'viewClient', params: { id: order.client.id } }"
                class="budget-stat-value text-primary"
              >{{ order.client.name }}</b-link>
              <span
                v-else
                class="budget-stat-value"
              >{{ order.client.name }}</span>
            </div>
            <div
              v-if="order.user"
              class="budget-stat"
            >
              <span class="budget-stat-label">{{ $t('configurator.detail.responsible') }}</span>
              <span class="budget-stat-value text-truncate">{{ order.user.email }}</span>
            </div>
          </div>
        </div>

        <!-- Entrega -->
        <div class="cfg-card">
          <div class="budget-header">
            <span class="budget-title">{{ $t('configurator.delivery') }}</span>
          </div>
          <div class="px-1 py-75">
            <template v-if="order.address && order.address.address">
              <div class="font-weight-bold small">
                {{ order.address.address }}
              </div>
              <div class="cfg-hint">
                {{ [order.address.postalCode, order.address.town, order.address.province, order.address.country].filter(Boolean).join(', ') }}
              </div>
              <div
                v-if="order.address.phone"
                class="cfg-hint"
              >
                {{ order.address.phone }}
              </div>
            </template>
            <div
              v-else-if="status === 'pending'"
              class="cfg-hint"
            >
              {{ $t('configurator.detail.delivery_on_finish') }}
            </div>
            <div
              v-else
              class="small font-weight-bold"
            >
              {{ $t('configurator.pickup_tecnitex') }}
            </div>
            <div
              v-if="order.shipping_pending"
              class="budget-warning mt-50"
            >
              {{ $t('configurator.detail.transport_pending_note') }}
            </div>
            <div
              v-if="order.install"
              class="budget-note mt-50"
            >
              {{ $t('configurator.detail.installation_requested') }}
            </div>
          </div>
        </div>

        <!-- Documentos -->
        <div class="cfg-card">
          <div class="budget-header d-flex justify-content-between align-items-center">
            <span class="budget-title">{{ $t('configurator.list.documents') }}</span>
            <a
              v-if="isSuperAdmin"
              href="#"
              class="small font-weight-bold"
              @click.prevent="showUpload = !showUpload"
            >+ {{ $t('configurator.detail.upload_documents') }}</a>
          </div>
          <div class="px-1 py-75">
            <div class="doc-links">
              <a
                v-if="order.budget"
                :href="order.budget.path"
                target="_blank"
                class="doc-link"
              >
                <feather-icon
                  icon="FileTextIcon"
                  size="13"
                />
                {{ $t('configurator.list.quote') }}
              </a>
              <a
                v-if="order.delivery_note"
                :href="order.delivery_note.path"
                target="_blank"
                class="doc-link"
              >
                <feather-icon
                  icon="TruckIcon"
                  size="13"
                />
                {{ $t('configurator.list.delivery_note') }}
              </a>
              <a
                v-if="order.invoice"
                :href="order.invoice.path"
                target="_blank"
                class="doc-link"
              >
                <feather-icon
                  icon="FileIcon"
                  size="13"
                />
                {{ $t('configurator.list.invoice') }}
              </a>
              <span
                v-if="!order.budget && !order.delivery_note && !order.invoice"
                class="cfg-hint"
              >{{ $t('configurator.detail.no_documents') }}</span>
            </div>
            <div
              v-if="showUpload"
              class="upload-box"
            >
              <div class="cfg-label">
                {{ $t('configurator.list.delivery_note') }} (PDF)
              </div>
              <b-form-file
                v-model="form.deliveryNote"
                accept="application/pdf"
                size="sm"
                :placeholder="$t('configurator.detail.choose_file')"
                :browse-text="$t('configurator.detail.browse')"
              />
              <div class="cfg-label mt-75">
                {{ $t('configurator.list.invoice') }} (PDF)
              </div>
              <b-form-file
                v-model="form.invoice"
                accept="application/pdf"
                size="sm"
                :placeholder="$t('configurator.detail.choose_file')"
                :browse-text="$t('configurator.detail.browse')"
              />
              <b-button
                variant="primary"
                class="cfg-btn mt-75"
                :disabled="!form.deliveryNote && !form.invoice"
                @click="uploadDocs"
              >
                {{ $t('configurator.detail.upload') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
      <div>
        <offer-totals
          :order="order"
          :always-show-installation="isSuperAdmin && inRevision"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { BButton, BFormFile, BLink } from 'bootstrap-vue'
import { TimestampToFlatPickr, TimestampToFlatPickrOnlyYear } from '@/libs/helpers'
import OfferLines from '@/views/configurator/components/OfferLines.vue'
import OfferTotals from '@/views/configurator/components/OfferTotals.vue'
import OrderTracking from './components/OrderTracking.vue'
import NextStep from './components/NextStep.vue'

// Pantalla única de un pedido / oferta para todos los roles. Las herramientas de Tecnitex
// (revisión, envío, documentos) aparecen dentro de "Siguiente paso" según el estado.
export default {
  components: {
    BButton, BFormFile, BLink, OfferLines, OfferTotals, OrderTracking, NextStep,
  },
  data() {
    return {
      showUpload: false,
      highlightLines: false,
      form: {
        deliveryNote: null,
        invoice: null,
      },
    }
  },
  computed: {
    ...mapGetters({
      order: 'offer/getOrder',
      configurator: 'offer/getConfigurator',
      currentUser: 'auth/getUser',
    }),
    role() {
      return this.currentUser.roles[0].name
    },
    isSuperAdmin() {
      return this.role === 'super_admin'
    },
    status() {
      return this.order.status_last ? this.order.status_last.type : 'pending'
    },
    statusClass() {
      if (this.status === 'pending') return 'cfg-badge-orange'
      if (this.status === 'revision') return 'cfg-badge-primary'
      if (['ready', 'send', 'cast', 'delivered'].includes(this.status)) return 'cfg-badge-green'
      return 'cfg-badge-gray'
    },
    offerNumber() {
      return `${TimestampToFlatPickrOnlyYear(this.order.created_at)}/T${this.order.id}`
    },
    created() {
      return this.date(this.order.created_at)
    },
    legacy() {
      return !this.configurator
    },
    showClient() {
      return this.role !== 'user'
    },
    inRevision() {
      return this.status === 'revision'
    },
  },
  watch: {
    '$route.params.id': function reload(id) {
      if (id) this.load(id)
    },
  },
  async created() {
    this.$store.commit('offer/clear')
    await this.load(this.$route.params.id)
  },
  methods: {
    ...mapActions({
      load: 'offer/load',
      setLinePrice: 'offer/setLinePrice',
      uploadDocuments: 'offer/uploadDocuments',
    }),
    date(timestamp) {
      return TimestampToFlatPickr(timestamp)
    },
    back() {
      this.$router.push({ name: 'orders' })
    },
    // Desde la checklist de revisión: baja a las líneas y resalta las que están en Validación OT
    goToLines() {
      this.$refs.lines.scrollIntoView({ behavior: 'smooth', block: 'start' })
      this.highlightLines = true
      setTimeout(() => { this.highlightLines = false }, 2500)
    },
    async uploadDocs() {
      const data = new FormData()
      if (this.form.deliveryNote) data.append('albaran[]', this.form.deliveryNote)
      if (this.form.invoice) data.append('factura[]', this.form.invoice)
      if (await this.uploadDocuments(data)) {
        this.form.deliveryNote = null
        this.form.invoice = null
        this.showUpload = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/views/configurator/configurator.scss';
</style>

<style lang="scss" scoped>
.detail-bottom {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 1.25rem;
  align-items: start;
  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
}
.detail-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
  align-items: start;
  .cfg-card {
    margin-bottom: 0;
  }
}
.lines-highlight ::v-deep .price-input input {
  box-shadow: 0 0 0 3px rgba(255, 159, 67, .45);
}
.doc-links {
  display: flex;
  flex-wrap: wrap;
  gap: .35rem;
}
.doc-link {
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  padding: .2rem .55rem;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  font-size: .72rem;
  font-weight: 600;
  color: #4d4d4d;
  &:hover {
    border-color: #ac1a19;
    color: #ac1a19;
  }
}
.upload-box {
  margin-top: .75rem;
  padding-top: .75rem;
  border-top: 1px solid #f0f0f0;
}
</style>
